import { useCallback, useState } from 'react'
import { Pallets } from '../../../../../../modules/API/Pallets/pallets'
import { IPalletListWithDetails } from '../../../../../../modules/API/Pallets/models/palletList'
import { handleNotifications } from '../../../../../../generic/utils/handleNotifications'
import { isValidPalletNumber } from '../helpers/palletPayloads'

export interface UsePalletLookupResult {
  palletNumber: string
  setPalletNumber: (value: string) => void
  isExternal: boolean
  setIsExternal: (value: boolean) => void
  palletFound: IPalletListWithDetails | null
  findPallet: (value?: string) => Promise<void>
  reset: () => void
}

/**
 * Shared pallet-lookup logic used by the pallet/location and pallet/editcustomer
 * screens: holds the scanned/typed pallet number, the internal/external toggle and
 * the fetched pallet. `reset` clears the number and the found pallet but keeps the
 * toggle, matching the legacy clear behaviour.
 */
const usePalletLookup = (): UsePalletLookupResult => {
  const [palletNumber, setPalletNumber] = useState<string>('')
  const [isExternal, setIsExternal] = useState<boolean>(false)
  const [palletFound, setPalletFound] = useState<IPalletListWithDetails | null>(null)

  const findPallet = useCallback(
    async (value?: string) => {
      const raw = value ?? palletNumber
      if (!isValidPalletNumber(raw)) {
        handleNotifications('מספר משטח לא תקין', { type: 'warning' })
        return
      }
      try {
        const res = await Pallets.getPalletWithDitailesByPalletNum(Number(raw), isExternal ? 1 : 0)
        setPalletFound(res)
      } catch (error) {
        setPalletFound(null)
        handleNotifications('משטח לא נמצא', { type: 'error' })
      }
    },
    [palletNumber, isExternal],
  )

  const reset = useCallback(() => {
    setPalletNumber('')
    setPalletFound(null)
  }, [])

  return {
    palletNumber,
    setPalletNumber,
    isExternal,
    setIsExternal,
    palletFound,
    findPallet,
    reset,
  }
}

export default usePalletLookup
